import { IndicatorNumber, TextInfoGroupAlt } from "@/components/shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card } from "@/components/ui/card";
import { SiteVisit, TenderDurationFragment } from "@/graphql/__generated__/graphql";
import { convertBpToDecimal, formatDateLocale, formatNumberLocale } from "@/utils/helpers";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { IntlShape, useIntl } from "react-intl";

/**
 * getDateRangeString - This function returns the date range string for the site visit
 * @param {TFunction} t - The translation function
 * @param {IntlShape} intl - The intl object
 * @param {SiteVisit} siteVisit - The site visit object
 * @param {TenderDurationFragment} tenderDuration - The tender duration object
 */
const getDateRangeString = (t: TFunction, intl: IntlShape, siteVisit: SiteVisit, tenderDuration: TenderDurationFragment): string => {
  if (!siteVisit.startDate && !siteVisit.endDate) {
    return t("common:entirecontractduration");
  }

  const startDate = siteVisit.startDate ? formatDateLocale(intl, siteVisit.startDate) : formatDateLocale(intl, tenderDuration.contract.startDate);
  const endDate = siteVisit.endDate ? formatDateLocale(intl, siteVisit.endDate) : formatDateLocale(intl, tenderDuration.contract.endDate);
  return `${startDate} - ${endDate}`;
};

interface Props {
  siteVisits: Partial<SiteVisit>[];
  tenderDuration: TenderDurationFragment;
}

/**
 * SiteVisitsSection - Renders the site visits accordion section in the Calculation Overview sidebar.
 *
 * @component
 * @example
 * <SiteVisitsSection siteVisits={service.siteVisits} tenderDuration={tenderDuration} />
 */
function SiteVisitsSection({ siteVisits, tenderDuration }: Props) {
  const { t } = useTranslation();
  const intl = useIntl();

  const hasSiteVisits = (siteVisits?.length ?? 0) > 0;

  return (
    <Card className="shadow-none">
      <Accordion type="single" collapsible>
        <AccordionItem value="site-visits">
          <AccordionTrigger className="hover:no-underline hover:bg-slate-50 px-4 py-4">
            <div className="flex items-center gap-2 w-full">
              <div className="text-sm font-bold grow">{t("edittender.calculationoverview.components.sitevisits.title")}</div>
              <IndicatorNumber number={siteVisits.length} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-2 pb-4">
            {!hasSiteVisits ? (
              <div className="text-sm text-slate-500 text-center pt-4">
                {t("edittender.calculationoverview.components.sitevisits.empty")}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {siteVisits?.map((siteVisit: SiteVisit, index: number) => (
                  <Card className="p-4 shadow-none" key={`site-visit-${index}`}>
                    <div className="flex items-center justify-between mb-4">
                      {/* Site Visit Details */}
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {/* Type */}
                        <TextInfoGroupAlt label={t("addsitevisit.type")} content={t("common:sitevisittype." + siteVisit.type.toLowerCase())} />
                        {/* Margin */}
                        <TextInfoGroupAlt label={t("addsitevisit.margin")} content={`${convertBpToDecimal(siteVisit.margin)} %`} />
                        {/* Duration */}
                        <TextInfoGroupAlt label={t("addsitevisit.duration")} content={`${siteVisit?.duration ?? ""} ${t("common:minutes").toLowerCase()}`} />
                        {/* PricePerHour */}
                        <TextInfoGroupAlt label={t("addsitevisit.pricepersitevisit")} content={formatNumberLocale(intl, (siteVisit as SiteVisit)?.pricePerSiteVisit ?? 0, "currency")} />
                        {/* Date Range */}
                        <TextInfoGroupAlt label={t("addsitevisit.daterange")} content={getDateRangeString(t, intl, siteVisit, tenderDuration)} />
                      </div>
                    </div>

                    {/* Site Visit Days and Nights */}
                    <div className="text-sm">
                      <div className="flex justify-end gap-2 text-right">
                        <span className="w-10">{t("common:day")}</span>
                        <span className="w-10">{t("common:night")}</span>
                      </div>

                      <div className="flex flex-col gap-1">
                        {["Monday", "Tuesday", "Wednesday", "Thursday",
                          "Friday", "Saturday", "Sunday", "Holiday"].map((day) => (
                          typeof siteVisit[`${day.toLowerCase()}Day`] === "number" &&
                            typeof siteVisit[`${day.toLowerCase()}Night`] === "number" && (
                            <div key={day} className="flex justify-between hover:bg-slate-50 rounded-sm px-1">
                              <span>{t(`common:weekdaysfull.${day.toLowerCase()}`)}</span>
                              <div className="flex gap-2 text-right">
                                <span className="w-10">{siteVisit[`${day.toLowerCase()}Day`]}</span>
                                <span className="w-10">{siteVisit[`${day.toLowerCase()}Night`]}</span>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

export default SiteVisitsSection;
